import { Box, Divider } from '@mui/material';
import EAButton from 'components/common/EAButton';
import EAIcon from 'components/common/EAIcon';
import i18next from 'i18next';
import { useContext, useEffect, useState } from 'react';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import useStyles from './CreditsafePanelStyle';
import {
  getCreditsafeScoreForThirdParty,
  getCreditsafeScoreForThirdPartyForceUpdate,
  getCreditsafeScoreHistory,
  searchCreditsafeCompanyForThirdParty,
  stopCreditsafeScoreForThirdParty,
} from 'services/edge/CreditsafeService';
import { Context } from 'states/Store';
import { theme } from 'style/globalStyle';
import GlobalScoreGauge from '../scores/GlobalScoreGauge';
import IconeEvolutionScore from '../scores/IconeEvolutionScore';
import { ERROR } from 'services/common/Constants';
import {
  CredifsafeScore,
  getCreditsafeColorDependingOnScore,
} from 'services/common/CreditsafeUtils';
import clsx from 'clsx';
import AdditionalFinancialDataModal from './AdditionalFinancialDataModal';
import NationalScoreDetailsModal from './NationalScoreDetailsModal';
import InternationalScoreDetailsModal from './InternationalScoreDetailsModal';
import OtherDetailsModal from './OtherDetailsModal';
import ScoresButtons from './ScoresButtons';
import EADialog from 'components/common/EADialog';
import ProcessPendingBloc from 'components/common/ProcessPendingBloc';
import EAColorIcon from 'components/common/EAColorIcon';
import { formatNumberCurrency } from 'services/common/Utils';

const NoEvaluation = ({ startCreditsafeEvaluation = () => {} }) => {
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  return (
    <Box className={classes.rootNoData}>
      <EAIcon icon="search_off" />
      <div className={classes.noDataText}>
        {i18next.t('financialStrength.scoreNotActivated')}
      </div>

      <EAButton
        colorVariant="primaryLight"
        onClick={startCreditsafeEvaluation}
        disabled={
          !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CREDITSAFE_SOURCE)
        }
        className={classes.button}
        customStyle={{ boxShadow: 'none' }}
        content={
          <span
            className={clsx(
              classes.buttonContent,
              classes.buttonPrimaryContent
            )}
          >
            <EAColorIcon icon="newEnrichment" />
            {i18next.t('creditsafe.activation')}
          </span>
        }
      />
    </Box>
  );
};

const ScoreDetailLine = ({ score, value }) => {
  const { classes } = useStyles();
  const [iScoreDetailModalOpen, setScoreDetailModalOpen] = useState(false);

  return (
    <>
      <div className={classes.scoreDetailsContent}>
        <span
          className={classes.squareScore}
          style={{
            backgroundColor: getCreditsafeColorDependingOnScore(
              score,
              value.toUpperCase()
            ),
          }}
        >
          {score === 'preferentialStatus'
            ? value
              ? i18next.t(`creditsafe.preferentialValues.${value}`)
              : '-'
            : value}
        </span>
        {i18next.t(`creditsafe.details.${score}.short`)}
        <span
          className={classes.openDetailsbutton}
          onClick={() => setScoreDetailModalOpen(true)}
        >
          <EAIcon icon="call_made" />
        </span>
      </div>

      <OtherDetailsModal
        score={score}
        value={
          score === 'preferentialStatus'
            ? value
              ? i18next.t(`creditsafe.preferentialValues.${value}`)
              : '-'
            : value
        }
        color={getCreditsafeColorDependingOnScore(score, value.toUpperCase())}
        isOpen={iScoreDetailModalOpen}
        onClose={() => setScoreDetailModalOpen(false)}
      />
    </>
  );
};

const ScoresDetails = ({ data, thirdPartyId }) => {
  const { classes } = useStyles();

  const [
    openAdditionalFinancialDataModal,
    setOpenAdditionalFinancialDataModal,
  ] = useState(false);
  const [openOtherDetailsModal, setOpenOtherDetailsModal] = useState(false);

  return (
    <div className={classes.scoreDetails}>
      <div>
        <ScoreDetailLine score={'creditLimit'} value={formatNumberCurrency(data?.creditLimitValue, data?.creditLimitCurrency)} />

        <ScoreDetailLine
          score={'preferentialStatus'}
          value={data?.preferentialStatus}
        />

        <ScoreDetailLine score={'dbt'} value={data?.dbt ? data?.dbt : 'N/A'} />

        {data.countryCode === 'FR' && (
          <div className={classes.scoreDetailsContent}>
            {i18next.t('creditsafe.scores.additionalFinancialData')}
            <span
              className={classes.openDetailsbutton}
              onClick={() => setOpenAdditionalFinancialDataModal(true)}
            >
              <EAIcon icon="call_made" />
            </span>
          </div>
        )}
      </div>

      <div className={classes.dataDepthSection}>
        <ScoreDetailLine
          score={'financialStrength'}
          value={data?.financialStrength ? data?.financialStrength : '-'}
        />

        <div className={classes.scoreDetailsSection}>
          {i18next.t('creditsafe.scores.exposure')}
        </div>

        <ScoreDetailLine
          score={'activityExposure'}
          value={data?.activityExposure ? data?.activityExposure : '-'}
        />

        <ScoreDetailLine
          score={'companyExposure'}
          value={data?.companyExposure ? data?.companyExposure : '-'}
        />
      </div>

      <AdditionalFinancialDataModal
        thirdPartyId={thirdPartyId}
        isOpen={openAdditionalFinancialDataModal}
        onClose={() => setOpenAdditionalFinancialDataModal(false)}
      />
      <OtherDetailsModal
        isOpen={openOtherDetailsModal}
        onClose={() => setOpenOtherDetailsModal(false)}
        data={data}
      />
    </div>
  );
};

const GlobalScoreCreditsafe = ({ data }) => {
  const { classes } = useStyles();

  const score = data?.internationalScore;

  const [
    openNationalScoreDetailsModal,
    setOpenNationalScoreDetailsModal,
  ] = useState(false);
  
  const [
    openInternationalScoreDetailsModal,
    setOpenInternationalScoreDetailsModal,
  ] = useState(false);

  const scoreStyle =
    score === 'D'
      ? {
          background: classes.score5background,
          text: i18next.t('creditsafe.scoresValues.high.short'),
          mainColor: theme.palette.error.error600,
          secondaryColor: theme.palette.error.error100,
        }
      : score === 'C'
      ? {
          background: classes.score3background,
          text: i18next.t('creditsafe.scoresValues.medium.short'),
          mainColor: theme.palette.warning.warning500,
          textColor: theme.palette.warning.warning600,
          secondaryColor: theme.palette.warning.warning100,
        }
      : score === 'B'
      ? {
          background: classes.score2background,
          text: i18next.t('creditsafe.scoresValues.low.short'),
          mainColor: theme.palette.primary.primary400,
          secondaryColor: theme.palette.primary.primary100,
        }
      : score === 'A'
      ? {
          background: classes.score1background,
          text: i18next.t('creditsafe.scoresValues.very_low.short'),
          mainColor: '#2D9F40',
          secondaryColor: theme.palette.primary.primary200,
        }
      : {
          background: classes.scoreUnknownBackground,
          text: i18next.t('creditsafe.scoresValues.no_score.short'),
          mainColor: theme.palette.grey.grey400,
          secondaryColor: theme.palette.grey.grey100,
        };

  const getScoreByValue = (value) => {
    if (value === 'A') {
      return CredifsafeScore.A;
    }
    if (value === 'B') {
      return CredifsafeScore.B;
    }
    if (value === 'C') {
      return CredifsafeScore.C;
    }
    if (value === 'D') {
      return CredifsafeScore.D;
    }
    return CredifsafeScore.E;
  };

  const isRiskLower = (value, previousValue) => {
    let compare = value.localeCompare(previousValue);
    if (compare === 0) {
      return null;
    } else if (compare === 1) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <div className={classes.scoreGraph}>
        <div className={clsx(scoreStyle.background)}>
          <div className={classes.flex}>
            <div className={classes.scoreColumn}>
              <div
                className={classes.scoreLabel}
                style={{ marginRight: '20px' }}
              >
                <span>
                  {i18next.t('creditsafe.scores.internationalScoreShort')}
                </span>
                <span
                  className={classes.openGlobalDetailsbutton}
                  onClick={() => setOpenInternationalScoreDetailsModal(true)}
                >
                  <EAIcon icon="call_made" />
                </span>
              </div>
              <GlobalScoreGauge
                data={data}
                scoreName={'internationalScore'}
                maxScore={''}
                folderName={'creditsafe'}
                getScoreByValue={getScoreByValue}
                clazz={classes.gaugeStyle}
              />
            </div>
            {data.countryCode === 'FR' && (
              <>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  style={{ borderColor: '#E2E2EA' }}
                  flexItem
                />
                <div className={classes.scoreColumn}>
                  <div className={clsx(classes.scoreLabel)}>
                    <span>
                      {i18next.t('creditsafe.scores.nationalScoreValueShort')}
                    </span>
                    <span
                      className={classes.openGlobalDetailsbutton}
                      onClick={() => setOpenNationalScoreDetailsModal(true)}
                    >
                      <EAIcon icon="call_made" />
                    </span>
                  </div>
                  <div className={classes.globalScoreChart}>
                    <div className={classes.nationalScore}>
                      {data['nationalScoreValue'] !== 'Not scored' ? (
                        <>
                      <span className="score">
                        {data['nationalScoreValue']}
                      </span>
                      {data.nationalScoreMax && (
                        <span className="max-score">
                          {'/' + data.nationalScoreMax}
                        </span>
                      )}
                    </>
                    ) : (
                      <span className="no-score">
                        {i18next.t('creditsafe.localScoresValues.no_score')}
                      </span>
                    )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={classes.flexCenter}>
            <span
              className={classes.globalScoreText}
              style={{
                backgroundColor: scoreStyle.secondaryColor,
                color: scoreStyle.mainColor,
              }}
            >
              {scoreStyle.text}
              <IconeEvolutionScore
                riskStyle={scoreStyle}
                isRiskLower={isRiskLower(
                  data['internationalScore'],
                  data['previousInternationalScore']
                )}
              />
            </span>
          </div>
        </div>
      </div>

      <NationalScoreDetailsModal
        isOpen={openNationalScoreDetailsModal}
        onClose={() => setOpenNationalScoreDetailsModal(false)}
      />
      <InternationalScoreDetailsModal
        isOpen={openInternationalScoreDetailsModal}
        onClose={() => setOpenInternationalScoreDetailsModal(false)}
      />
    </>
  );
};

const CreditsafePanel = ({ data, thirdPartyId, onRiskChange = () => {} }) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [creditsafeScore, setCreditsafeScore] = useState();
  const [creditsafeHistory, setCreditsafeHistory] = useState([]);
  const [isWaitingScore, setIsWaitingScore] = useState(false);
  const [isConfirmUpdateOpen, setIsConfirmUpdateOpen] = useState(false);

  const isEmpty = (object) => {
    if (object === undefined) {
      return true;
    } else {
      return Object.values(object).every((x) => x === null || x === '');
    }
  };

  useEffect(() => {
    if (data) {
      setCreditsafeScore(data);
    }
  }, [data]);

  // Fetch Scores History
  useEffect(() => {
    async function fetchCreditsafeScoreHistory() {
      try {
        const result = await getCreditsafeScoreHistory(
          state.account.id,
          thirdPartyId
        );
        setCreditsafeHistory(result.content);
      } catch (e) {
        setCreditsafeHistory();
      }
    }
    if (state.account && thirdPartyId) {
      fetchCreditsafeScoreHistory();
    }
  }, [state.account, thirdPartyId, data]);

  const startCreditsafeEvaluation = async () => {
    setIsWaitingScore(true);
    try {
      const reponse = await searchCreditsafeCompanyForThirdParty(
        state.account.id,
        thirdPartyId
      );
      if (!reponse.content.startsWith('ERROR : ')) {
        const scoreReponse = await getCreditsafeScoreForThirdParty(
          state.account.id,
          thirdPartyId
        );
        setCreditsafeScore(scoreReponse.content);
      } else {
        setCreditsafeScore({ error: reponse.content.creditsafe.error });
      }
      onRiskChange && onRiskChange();
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'ACTIVATE_CREDITSAFE_ERROR' },
      });
    }
    setIsWaitingScore(false);
  };

  const stopCreditsafe = async () => {
    try {
      await stopCreditsafeScoreForThirdParty(state.account.id, thirdPartyId);
      setCreditsafeScore({
        score: null,
      });
      onRiskChange && (await onRiskChange());
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'DEACTIVATE_CREDITSAFE_ERROR' },
      });
    }
  };

  const forceUpdateCreditsafe = async () => {
    setIsWaitingScore(true);
    setIsConfirmUpdateOpen(false);
    try {
      const reponse = await searchCreditsafeCompanyForThirdParty(
        state.account.id,
        thirdPartyId
      );
      if (!reponse.content.startsWith('ERROR : ')) {
        const scoreReponse = await getCreditsafeScoreForThirdPartyForceUpdate(
          state.account.id,
          thirdPartyId
        );
        setCreditsafeScore(scoreReponse.content);
      } else {
        setCreditsafeScore({ error: reponse.content.creditsafe.error });
      }
      onRiskChange && onRiskChange();
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'ACTIVATE_CREDITSAFE_ERROR' },
      });
    }
    setIsWaitingScore(false);
  };

  return (
    <>
      {!isEmpty(creditsafeScore) && creditsafeScore?.error == null ? (
        <div className={classes.content}>
          <div>
            <div className={classes.actionsTopButtons}>
              <span style={{ fontSize: '12px', fontWeight: '600' }}>
                {i18next.t('creditsafe.scores.score')}
              </span>
              <div style={{ display: 'flex' }}>
                <ScoresButtons
                  stopCreditsafe={stopCreditsafe}
                  scoresHistory={creditsafeHistory}
                />
              </div>
            </div>
            <div className={classes.content}>
              <GlobalScoreCreditsafe data={creditsafeScore} />
              <ScoresDetails
                data={creditsafeScore}
                thirdPartyId={thirdPartyId}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {creditsafeScore?.error != null ? (
            <>
              <Box className={classes.rootNoData}>
                {isWaitingScore ? (
                  <ProcessPendingBloc />
                ) : (
                  <>
                    {creditsafeScore.error ===
                    'REPORT_UNAVAILABLE_FOR_COUNTRY' ? (
                      <span>
                        {i18next.t(
                          `creditsafe.errors.${creditsafeScore.error}`
                        ) +
                          ' ' +
                          state.thirdparty?.addressCountryCode}
                      </span>
                    ) : (
                      <span>
                        {i18next.t(
                          `creditsafe.errors.${creditsafeScore.error}`
                        )}
                      </span>
                    )}
                    <div>
                      <EAButton
                        className={classes.updateButton}
                        size="small"
                        onClick={() => setIsConfirmUpdateOpen(true)}
                        colorVariant="primary"
                        content={
                          <>
                            <span>{i18next.t('creditsafe.updateScore')}</span>
                            <EAIcon icon="loop_white" />
                          </>
                        }
                      />
                    </div>
                  </>
                )}
              </Box>
            </>
          ) : (
            <>
              {isWaitingScore ? (
                <Box className={classes.rootNoData}>
                  <ProcessPendingBloc />
                </Box>
              ) : (
                <NoEvaluation
                  startCreditsafeEvaluation={startCreditsafeEvaluation}
                />
              )}
            </>
          )}
        </>
      )}
      <EADialog
        isOpen={isConfirmUpdateOpen}
        hasActions={false}
        onClose={() => setIsConfirmUpdateOpen(false)}
        maxWidth="md"
        title={i18next.t('creditsafe.confirmUpdateTitle')}
      >
        <div className={classes.dialogStop}>
          <div className={classes.dialogStopContent}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: '400',
                padding: '0px 10px',
              }}
            >
              {i18next.t('creditsafe.confirmUpdateText')}
            </div>
          </div>
          <div className={classes.dialogStopActions}>
            <EAButton
              onClick={() => setIsConfirmUpdateOpen(false)}
              content={i18next.t('cancel')}
              colorVariant="secondary"
            />
            <EAButton
              onClick={forceUpdateCreditsafe}
              content={i18next.t('validate')}
              colorVariant="warning"
            />
          </div>
        </div>
      </EADialog>
    </>
  );
};

export default CreditsafePanel;
